<template>
  <div class="pb-90">
    <div class="chooseBike wrapper mb-104">
      <a href="/en/catalog" class="backBtn">
        <svg
          fill="none"
          height="12"
          viewBox="0 0 8 12"
          width="8"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0.999999 11.4531L6 6.45313L1 1.45312"
            stroke="black"
            stroke-width="1.5"
          />
        </svg>
        All bikes
      </a>
      <div class="grid">
        <div class="col x-6">
          <div class="sticky-wrap">
            <slider :full="false" :zoom-models="zoomModels" :custom-build="build"/>
          </div>
        </div>
        <div class="col x-6">
          <p class="fs-36 color-black bold mt-0 mb-32">
            {{ buildName }}
          </p>
          <div class="w-full mb-32 d-flex">
            <p class="fs-24 color-black bold m-0 mr-1">
              {{ buildPrice }}
            </p>
            <div class="ui-tooltip ml-1">
              <button type="button" class="ui-tooltip__btn"/>
              <div class="ui-tooltip__dropdown">prices</div>
            </div>
          </div>
          <div class="w-full mb-32">
            <p class="fs-24 color-black medium mt-0 mb-4">Colour</p>
            <div class="select-card-color">
              <div v-for="group in groups" :key="`color-group-${group.id}`" class="group">
                <div class="checkboxes">
                  <div
                    class="checkbox"
                    @click="selectColor(color)"
                    :class="isColorActive(color)"
                    v-for="color in group.colors"
                    :key="`group-${group.id}-color-${color.id}`"
                  >
                    <img
                      :alt="color.title"
                      :src="color.image"
                    >
                  </div>
                </div>
                <p class="fs-12 color-dark m-0 ml-2">{{ group.title }}</p>
              </div>
            </div>
          </div>
          <hr class="hr mt-0 mb-32">
          <div class="w-full">
            <p class="fs-24 color-black medium mt-0 mb-4">Size</p>
            <ul class="checkbox-list size-wrap p-0">
              <li
                v-for="size in sortedSizes"
                :key="`size-${size.id}`"
                class="checkboxEl"
                :class="isSizeActive(size)"
                @click="selectSize(size)"
              >
                <div class="stripe"/>
                <p class="titleSize">{{ size.title }}</p>
                <p class="textSize">{{ size.description }}</p>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    <div class="wrapper mb-104">
      <ul class="ui-accordion">
        <li class="ui-accordion__item" :class="accordionClass('details')" v-if="hasProductBuildDescription">
          <div
            ref="details-header"
            class="ui-accordion__item--title accordion-header"
            @click="switchAccordion('details')"
          >
            Product details
          </div>
          <transition
            name="accordion"
            @before-enter="beforeEnter"
            @enter="enter"
            @after-enter="afterEnter"
            @leave="leave"
          >
            <div
              class="ui-accordion__item--content"
              v-show="activeAccordion === 'details'"
              v-html="productBuild.data.product_description"
            >
            </div>
          </transition>
        </li>
        <li
          class="ui-accordion__item"
          :class="accordionClass('specs')"
          v-if="hasSpecs"
        >
          <div
            ref="specs-header"
            class="ui-accordion__item--title"
            @click="switchAccordion('specs')"
            id="tech-spec-block"
          >
            Technical specifications
          </div>
          <transition
            name="accordion"
            @before-enter="beforeEnter"
            @enter="enter"
            @after-enter="afterEnter"
            @leave="leave"
          >
            <div v-show="activeAccordion === 'specs'" class="ui-accordion__item--content">
              <div class="comparison-wrapper spec-section mb-0 show-one">
                <div class="spec-section-item" v-for="spec in specs" :key="`spec-${spec.id}`">
                  <div class="titleComp">
                    <img alt="Suspension" :src="specIcon(spec)" class="ic">
                    <p class="text">{{ spec.common_title }}</p>
                    <hr>
                  </div>
                  <div class="text-wrap">
                    <p class="fs-17 bold">{{ spec.title }}</p>
                    <p class="fs-17 color-gray">{{ spec.description }}</p>
                  </div>
                </div>
              </div>
              <button class="btn-gray w-full mt-32" type="button" @click="toggleSpecFullList">
                {{ fullSpecList ? "Show Less" : "Show more" }}
              </button>
            </div>
          </transition>
        </li>
        <li
          class="ui-accordion__item"
          :class="accordionClass('geometry')"
          v-if="model?.slug"
        >
          <div
            class="ui-accordion__item--title"
            @click="switchAccordion('geometry')"
            ref="geometry-header"
          >
            Geometry
          </div>
          <transition
            name="accordion"
            @before-enter="beforeEnter"
            @enter="enter"
            @after-enter="afterEnter"
            @leave="leave"
          >
            <div v-show="activeAccordion === 'geometry'" class="ui-accordion__item--content">
              <geometry-table :trans="translates" :model="model.slug"/>
            </div>
          </transition>
        </li>
        <li class="ui-accordion__item" v-if="manuals.length > 0" :class="accordionClass('manuals')">
          <div
            class="ui-accordion__item--title"
            @click="switchAccordion('manuals')"
            ref="manuals-header"
          >
            Manual downloads
          </div>
          <transition
            name="accordion"
            @before-enter="beforeEnter"
            @enter="enter"
            @after-enter="afterEnter"
            @leave="leave"
          >
            <div v-show="activeAccordion === 'manuals'" class="ui-accordion__item--content">
              <div class="grid-6">
                <a target="_blank" v-for="manual in manuals" :href="manual.file" class="media-card">
                  <div class="media-card__img">
                    <img src="https://dev.forestal.com/assets/images/icons/pdf.svg" :alt="manual.name">
                  </div>
                  <span class="media-card__text">
                    {{ manual.name }}
                  </span>
                </a>
              </div>
            </div>
          </transition>
        </li>
      </ul>
    </div>
    <div class="w-full mb-104" v-if="hasProductBuildBlock">
      <div class="main-motor bg-white p-0">
        <div class="motor-box__wrapper">

          <div
            class="main-motor-box one"
            :key="`pb-block-${block.id}`"
            v-for="block in productBuildBlocks"
            :class="pbBlockClass(block)"
          >
            <div class="main-motor-box__image">
              <img :src="block.image"
                   :data-src="block.image"
                   :alt="block.text">
            </div>
            <div class="main-motor-box__text" v-html="block.text"></div>
          </div>
        </div>
      </div>
      <div class="wrapper" v-if="showMoreBlocksVisibility">
        <button class="btn-gray w-full" type="button" @click.prevent="showMoreBlocks">
          <template v-if="!showAllBlocks">Show more</template>
          <template v-else>Show less</template>
        </button>
      </div>
    </div>

    <div class="wrapper mb-104" v-if="hasProductSpecs">
      <div class="specs m-0">
        <div class="specs__col" v-for="spec in productBuildSpecs" :key="`pb-spec-${spec.id}`">
          <div class="specs__item"><h3>{{ spec.title }}</h3>
            <p>{{ spec.text }}</p></div>
        </div>
      </div>
    </div>

    <div class="main-suspension p-0 mb-104" v-if="productBuildFooterImage">
      <div class="main-suspension__image m-0">
        <img :src="productBuildFooterImage" :alt="buildName">
      </div>
    </div>

    <div class="wrapper mb-104">
      <p class="fs-36 bold color-black">
        Included in delivery
      </p>
      <div class="included-delivery">
        <included-in-delivery
          :trans="translates"
          :list="includedInDelivery"
          :without-title="true"
          :custom-greed="true"
        />
      </div>
    </div>
    <div class="carousel-models-wrapper">
      <div class="wrapper">
        <p class="fs-36 color-black bold">
          Find best model for you
        </p>
        <div class="carousel-models">
          <flickity ref="flickity" :options="flickityOptions">
            <div v-for="model in models" :key="`model-${model.id}`" class="carousel-cell">
              <div class="item">
                <div class="text-wrap">
                  <p class="title">{{ model.model }}</p>
                  <div class="text" v-html="model['carousel_description']"></div>
                  <p class="price">from {{ $crConverter(model.eu_build_min.cost / 100, 'EUR') }}</p>
                  <a v-if="model.purchasable" :href="`/en/choose/${model.slug}`" class="btn btn-green" target="_blank">Buy</a>
                  <a :href="`/en/products/${modelSlug(model)}`" class="btn btn-arr" target="_blank">
                    Learn More
                    <svg
                      fill="none"
                      height="12"
                      viewBox="0 0 8 12"
                      width="8"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M0.999999 11.4531L6 6.45313L1 1.45312"
                        stroke="black"
                        stroke-width="1.5"
                      />
                    </svg>
                  </a>
                </div>
                <div class="img-wrap">
                  <picture>
                    <source
                      :srcset="model['slider_img']"
                      media="(max-width: 991px)"
                    />
                    <img
                      :src="model['slider_img']"
                      class="picture"
                      alt=""/>
                  </picture>
                </div>
              </div>
            </div>
          </flickity>
        </div>
      </div>
    </div>

    <!-- modal -->
    <div
      :class="{ active: false }"
      class="modalPrimary modal-sm"
    >
      <button class="close max-md-hidden">x</button>
      <div class="modalBox text-center">
        <button class="close md-hidden">x</button>
        <p class="fs-36">The accessories box includes:</p>
        <ul class="modalBox__list">
          <li>
            Ringing bell
          </li>
          <li>
            Front light
          </li>
          <li>
            Rear light
          </li>
          <li>
            Reflectors kit
          </li>
        </ul>
        <button type="button" class="btn btn-green w-100">
          Ok, I understand
        </button>
      </div>
    </div>

    <info-footer v-if="!preview" :trans="translates" :custom-build="build"/>
  </div>
</template>
<script>

import Flickity from "vue-flickity";
import IncludedInDelivery from "./builds/parts/IncludedInDelivery.vue";
import CarouselSyncing from './builds/parts/BuildCarouselSync.vue'
import GeometryTable from './builds/parts/GeometryTable.vue'
import InfoFooter from "./builds/parts/InfoFooter.vue";

export default {
  name: "ProductCard",
  props: {
    model: {
      type: Object,
      required: true
    },
    trans: {
      type: Object,
      required: false,
      default: null
    },
    bike: {
      type: Object,
      required: true
    },
    groups: {
      type: Array,
      required: true
    },
    sizes: {
      type: Array,
      required: true
    },
    models: {
      type: Array,
      required: true
    },
    productBuild: {
      type: Object,
      required: false,
      default: null
    },
    preview: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  components: {
    'info-footer': InfoFooter,
    'geometry-table': GeometryTable,
    slider: CarouselSyncing,
    "included-in-delivery": IncludedInDelivery,
    "flickity": Flickity,
  },
  data() {
    return {
      showAllBlocks: false,
      refreshedBuild: null,
      selectedSize: null,
      selectedColor: null,
      zoomModels: [],
      activeAccordion: "details",
      fullSpecList: false,
      flickityOptions: {
        prevNextButtons: false,
        cellAlign: "left",
        percentPosition: true,
        contain: true,
        on: {
          staticClick: function (event, pointer, cellElement, cellIndex) {
            if (typeof cellIndex == "number") {
              this.selectCell(cellIndex);
            }
          },
        },
      },
    };
  },
  computed: {
    hasProductBuildDescription() {
      return this.productBuild?.data?.product_description || null
    },
    manuals() {
      return (this.productBuild?.data?.manuals) || []
    },
    productBuildFooterImage() {
      return this.productBuild?.data?.footer_image
    },
    productBuildSpecs() {
      return (this.productBuild?.data?.specs) || [];
    },
    hasProductSpecs() {
      return this.productBuildSpecs.length > 0;
    },
    productBuildBlocks() {
      let blocks = (this.productBuild?.data?.blocks || []).map((block, index) => {
        block.odd = index % 2 === 1
        return block
      })

      if (!this.showAllBlocks) {
        return blocks.slice(0, 3);
      }

      return blocks
    },
    showMoreBlocksVisibility() {
      return this.productBuildBlocks.length > 3
    },
    hasProductBuildBlock() {
      return (this.productBuild?.data?.blocks || []).length > 0;
    },
    build() {
      if (this.refreshedBuild) {
        return this.refreshedBuild
      }

      return this.bike
    },
    translates() {
      return this.trans.text['choose']
    },
    specs() {
      let filtered = this.build.specs.filter(item => {
        return !!item.common_title && item.title
      });

      if (!this.fullSpecList) {
        return filtered.slice(0, 8)
      }

      return filtered
    },
    hasSpecs() {
      return this.specs.length > 0;
    },
    includedInDelivery() {
      return this.build?.included_in_delivery || []
    },
    sortedSizes() {
      return this.sizes.sort((a, b) => {
        return b.id - a.id;
      })
    },
    buildName() {
      let title = ["Forestal"]
      if (this.build?.model_title) {
        title.push(this.build?.model_title)
      }

      if (this.build?.build_title?.title) {
        title.push(this.build?.build_title?.title)
      }

      if (this.build?.size?.title) {
        title.push(this.build?.size?.title + " Size")
      }
      return title.join(" ");
    },
    buildPrice() {
      return this.$crConverter(this.build?.prices?.price_for_eu / 100, 'EUR')
    }
  },
  methods: {
    scrollToActiveHeader(item) {
      this.$nextTick(() => {
        const header = this.$refs[`${item}-header`];
        if (header) {
          header.scrollIntoView({
            behavior: "smooth",
            block: "start"
          });

          setTimeout(() => {
            const headerOffset = 100; // отступ в пикселях
            const elementPosition =
              header.getBoundingClientRect().top + window.scrollY;
            const offsetPosition = elementPosition - headerOffset;

            window.scrollTo({
              top: offsetPosition,
              behavior: "smooth",
            });
          }, 400)
        }
      });
    },
    showMoreBlocks() {
      this.showAllBlocks = !this.showAllBlocks
    },
    pbBlockClass(block) {
      return {
        "main-motor-box--reverse": block.odd
      }
    },
    modelSlug(model) {
      if (model.slug.includes('e-')) {
        return model.slug.replace('e-', '')
      } else if (model.slug === 'cygnus' || model.slug === 'siryon') {
        return model.slug.replace('e-', '') + '/muscular'
      } else {
        return model.slug
      }
    },
    refreshBuild() {
      this.$store.dispatch("build/getBuild", {
        size: this.selectedSize,
        color: this.selectedColor,
        model: this.model.id
      }).then((build) => {
        this.refreshedBuild = build;

        let newUrl = ['en', 'catalog'];

        newUrl.push(build.model_slug);
        newUrl.push(build.build_title.slug);
        newUrl.push(build.color.slug);
        newUrl.push(build.id);

        window.history.replaceState(null, null, "/" + newUrl.join("/"));
      })
    },
    selectColor(color) {
      this.selectedColor = color.id;

      this.refreshBuild()
    },
    isColorActive(color) {
      return {
        active: this.selectedColor === color.id
      }
    },
    toggleSpecFullList() {
      this.fullSpecList = !this.fullSpecList;

      if (this.fullSpecList) {
        let table = document.getElementById('tech-spec-block')
        let topPoint = table.offsetTop + 800

        window.scrollTo({
          top: topPoint,
          behavior: 'smooth'
        });
      }
    },
    selectSize(size) {
      this.selectedSize = size.id;

      this.refreshBuild()
    },
    isSizeActive(size) {
      return {
        active: size.id === this.selectedSize
      };
    },
    accordionClass(item) {
      return {
        active: this.activeAccordion === item,
      }
    },
    switchAccordion(item) {
      if (this.activeAccordion === item) {
        this.activeAccordion = null;
      } else {
        this.activeAccordion = item;

        this.scrollToActiveHeader(item)
      }
    },
    specIcon(spec) {
      let specTitle = spec['common_title'].toLowerCase().replace('/', '-');
      return `/assets/images/spec_icons/${specTitle}.svg`
    },
    addBodyPadding() {
      let infoFooter = document.getElementsByClassName('resultFixLine')[0]
      document.getElementsByTagName("body")[0].style.paddingBottom = infoFooter.getBoundingClientRect().height + 'px'
    },
    beforeEnter(el) {
      el.style.height = '0';
      el.style.overflow = 'hidden';
    },
    enter(el) {
      el.style.height = el.scrollHeight + 'px';
    },
    afterEnter(el) {
      el.style.height = 'auto';
    },
    leave(el) {
      el.style.height = el.scrollHeight + 'px';
      el.offsetHeight;
      el.style.height = '0';
    }
  },
  mounted() {
    this.$store.dispatch('build/gallery').then(response => {
      this.zoomModels = [...response]
    })

    this.selectedSize = this.build.size.id
    this.selectedColor = this.build.color.id

    console.log("Model", this.model)
    console.log("Build", this.build)
    console.log("Colors", this.groups)
    console.log("Sizes", this.sizes)
    console.log("Models", this.models)
    console.log("ProductBuild", this.productBuild)

    this.addBodyPadding()
  }
};
</script>
<style lang="scss" scoped>

.mb-104 {
  margin-bottom: 104px;
  @media (max-width: 767px) {
    margin-bottom: 140px;
  }
}

.backBtn {
  margin-top: 34px;
  margin-bottom: 50px;
  @media (max-width: 991px) {
    display: inline-flex;
    margin-top: -2px;
    margin-bottom: 30px;
  }
}

.chooseBike {
  @media (max-width: 767px) {
    .col.x-6:first-of-type {
      margin-bottom: 40px;
    }
    .grid:last-child {
      padding-bottom: 0;
    }
  }

}

.select-card-color {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  flex-wrap: wrap;
  width: 100%;
  gap: 0 32px;
  margin-bottom: -20px;

  .group {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    flex-direction: column;
    margin-bottom: 20px;

    .checkboxes {
      width: 100%;
      display: flex;
      align-items: flex-start;
      max-width: 450px;
      flex-wrap: wrap;

      .checkbox {
        margin: 0 8px 8px 0;
        border: 2px solid transparent;
        padding: 2px;
        border-radius: 100%;
        display: block;
        cursor: pointer;
        transition: .4s;

        &.active {
          border-color: #000;
        }

        img {
          width: 34px;
          height: 34px;
          object-fit: contain;
          display: block;
          border-radius: 100%;
        }
      }
    }
  }
}

.hr {
  font-size: 0;
  height: 1px;
  border: none;
  background: #E3E3E3;
}

.grid-6 {
  display: grid;
  grid-gap: 20px 20px;
  width: 100%;
  grid-template-columns: repeat(6, 1fr);

  @media (max-width: 991px) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media (max-width: 767px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 575px) {
    grid-template-columns: repeat(1, 1fr);
  }
}

.last-mb-0:last-child {
  margin-bottom: 0 !important;
}

.specs__col {
  @media (min-width: 768px) {
    margin-bottom: 0;
  }
}

.carousel-models-wrapper {
  overflow: hidden;

  * {
    user-select: none;
  }

  .fs-36 {
    margin: 0 0 45px;
  }

  .flickity-slider {
    //margin-left: calc((100vw - 1158px) / 2);
  }

  .flickity-viewport {
    overflow: visible;
  }

  .carousel-cell {
    margin-right: 10px;
    overflow: hidden;
    border-radius: 25px;
    opacity: 0.5;
    -webkit-clip-path: content-box;
    clip-path: content-box;

    &.is-selected {
      opacity: 1;
    }

    .item {
      overflow: hidden;
      border-radius: inherit;
      width: 890px;
      background: #f9f9f9;
      padding: 56px;
      position: relative;

      transition: 0s;

      &:hover {
        .picture {
          transform: scale(1.03);
        }
      }

      .picture {
        position: absolute;
        bottom: 0;
        right: 0;
        height: 100%;
        max-width: 100%;
        object-fit: cover;
        transition: 0.3s;
      }

      .text-wrap {
        max-width: 324px;
        position: relative;
        z-index: 2;
      }

      .title {
        font-weight: bold;
        font-size: 42px;
        line-height: 1.1;
        color: #1c1c1c;
        margin: 0 0 10px;
      }

      .text {
        font-size: 17px;
        line-height: 25px;
        color: #474747;
        margin: 0 0 19px 0;
        height: 75px;
        overflow: hidden;

        p {
          margin: 0 0 8px;
          font-size: inherit;
          line-height: inherit;
          color: inherit;
        }
      }

      .price {
        font-weight: bold;
        font-size: 20px;
        line-height: 1.1;
        color: #1c1c1c;
        margin: 0 0 14px;
      }

      .btn.btn-green {
        border-radius: 7px;
        width: 174px;
        height: 40px;
        line-height: 1;
        display: flex;
        margin-bottom: 42px;
      }
    }
  }

  .flickity-page-dots {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    width: 100%;
    margin: 45px 0 0;

    .dot {
      width: 6px;
      height: 6px;
      border-radius: 100%;
      background-color: #c4c4c4;
      display: block;
      margin: 0 7px;
      border: none;
      opacity: 1;

      &:after {
        display: none;
      }

      &.is-selected {
        background-color: #c2e900;
      }
    }
  }

  @media (max-width: 1300px) {
    //.flickity-slider {
    //  margin-left: calc((100vw - 1020px) / 2);
    //}

    .carousel-cell .item {
      width: 700px;
    }
  }
  @media (max-width: 991px) {
    .fs-36 {
      margin: 0 0 35px;
    }
    .carousel-cell {
      opacity: 1;
      border-radius: 0;
      margin-right: 30px;

      .item {
        width: 374px;
        display: flex;
        flex-wrap: wrap;
        padding: 0;
        background-color: transparent;
        border-radius: 0;

        .img-wrap {
          order: 1;
          width: 100%;
          position: relative;
          padding-top: 90%;
          margin-bottom: 30px;
          background-color: #f9f9f9;
        }

        .text-wrap {
          order: 2;

          .title {
            font-size: 24px;
          }

          .text {
            font-size: 12px;
            line-height: 18px;
            height: 54px;
          }

          .btn.btn-green {
            margin: 0;
          }

          .btn.btn-arr {
            display: none;
          }
        }
      }
    }
    .flickity-page-dots {
      margin-top: 32px;
    }
  }
  @media (max-width: 767px) {
    .fs-36 {
      margin: 0 0 24px;
    }
    .carousel-cell {
      margin-right: 20px;

      .item {
        width: 294px;

        .img-wrap {
          margin-bottom: 26px;
        }
      }
    }
  }
  @media (max-width: 350px) {
    .carousel-cell {
      .item {
        width: 240px;
      }
    }
  }
}

.main-motor {
  margin-bottom: 40px;

  @media (min-width: 768px) {
    .main-motor-box:not(:last-child) {
      margin-bottom: 0;
    }
  }
  @media (max-width: 767px) {
    margin-bottom: 60px;
  }
}
</style>
